<!-- PedidosTablaSeguimiento.vue — Tabla de seguimiento con sort y paginación completa -->
<template>
  <div>
    <!-- ══ TABLA ESCRITORIO ════════════════════════════════════ -->
    <div class="divisan-table-container shadow-lg d-none d-lg-block">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
        <p class="mt-3 text-cafe fw-bold small">Cargando pedidos...</p>
      </div>
      <table v-else class="table table-divisan table-pedidos align-middle mb-0">
        <colgroup>
          <col style="width: 12%" />
          <col style="width: 18%" />
          <col style="width: 11%" />
          <col style="width: 11%" />
          <col style="width: 10%" />
          <col style="width: 12%" />
          <col style="width: 13%" />
          <col style="width: 13%" />
        </colgroup>
        <thead>
          <tr>
            <th class="ps-4">
              <ColumnFilterMenu
                field="numero_pedido"
                label="Pedido"
                type="number"
                :filterable="false"
                :items="pedidos"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="(e) => $emit('update:sort', e)"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="cliente"
                label="Cliente"
                :filterable="false"
                :items="pedidos"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="(e) => $emit('update:sort', e)"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="fecha_recibo"
                label="Recibo"
                type="date"
                :filterable="false"
                :items="pedidos"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="(e) => $emit('update:sort', e)"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="fecha_compromiso"
                label="Compromiso"
                type="date"
                :filterable="false"
                :items="pedidos"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="(e) => $emit('update:sort', e)"
              />
            </th>
            <th class="text-center">
              <ColumnFilterMenu
                field="dias_restantes"
                label="Restantes"
                type="number"
                :filterable="false"
                :items="pedidos"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="(e) => $emit('update:sort', e)"
              />
            </th>
            <th class="text-center">
              <ColumnFilterMenu
                field="estatus_almacen"
                label="Almacén"
                :items="itemsParaFiltroEstatus"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                :modelValue="filtroEstatusSet"
                @update:modelValue="$emit('update:filtroEstatusSet', $event)"
                @update:sort="(e) => $emit('update:sort', e)"
              />
            </th>
            <th class="text-center">
              <ColumnFilterMenu
                field="estatus_cliente"
                label="Cliente"
                :filterable="false"
                :items="pedidos"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="(e) => $emit('update:sort', e)"
              />
            </th>
            <th class="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in pedidos" :key="p.numero_pedido">
            <td class="ps-4">
              <span class="badge-molde">{{ p.numero_pedido }}</span>
            </td>
            <td>
              <div class="cliente-cell">
                <span class="cliente-num">{{ p.cliente_numero }}</span>
                <span class="cliente-nombre">{{ p.cliente }}</span>
              </div>
            </td>
            <td class="fecha-texto">{{ formatFecha(p.fecha_recibo) }}</td>
            <td class="fecha-texto">
              <span v-if="p.fecha_compromiso" class="fw-bold text-verde-oscuro">
                {{ formatFecha(p.fecha_compromiso) }}
              </span>
              <span v-else class="text-muted fst-italic" style="font-size: 0.82rem"
                >Sin asignar</span
              >
            </td>
            <td class="text-center">
              <span :class="badgeDias(p.dias_restantes)">{{
                labelDias(p.dias_restantes)
              }}</span>
            </td>
            <td class="text-center">
              <span :class="badgeEstatusAlmacen(p.estatus_almacen)">{{
                labelEstatus(p.estatus_almacen)
              }}</span>
            </td>
            <td class="text-center">
              <span :class="badgeEstatusCliente(p.estatus_cliente)">{{
                labelEstatusCliente(p.estatus_cliente)
              }}</span>
            </td>
            <td class="text-center">
              <div class="d-flex justify-content-center gap-2">
                <button
                  class="btn-circular"
                  title="Ver detalle"
                  @click="$emit('ver-detalle', p)"
                >
                  <i class="bi bi-list-ul text-dark"></i>
                </button>
                <button
                  class="btn-circular"
                  title="Editar pedido"
                  @click="$emit('editar', p)"
                  :disabled="!esEditable(p)"
                  :class="{ 'btn-circular-disabled': !esEditable(p) }"
                >
                  <i class="bi bi-pencil text-verde"></i>
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="!loading && pedidos.length === 0">
            <td colspan="8" class="text-center py-5 text-cafe opacity-50 fw-bold">
              No se encontraron pedidos
            </td>
          </tr>
        </tbody>
      </table>

      <!-- ── Footer: selector por página + paginación ── -->
      <div
        class="pagination-wrapper d-flex justify-content-between align-items-center px-4 py-3"
      >
        <!-- Izquierda: selector de registros + conteo -->
        <div class="d-flex align-items-center gap-3">
          <div class="d-flex align-items-center gap-2">
            <span class="pag-label">Mostrar</span>
            <div class="por-pagina-wrapper">
              <select
                :value="porPagina"
                @change="$emit('cambiar-por-pagina', Number($event.target.value))"
                class="por-pagina-select"
              >
                <option v-for="op in opcionesPorPagina" :key="op" :value="op">
                  {{ op }}
                </option>
              </select>
            </div>
            <span class="pag-label">registros</span>
          </div>
          <span class="pag-rango">
            {{ rangoInicio }}–{{ rangoFin }} de {{ totalRegistros }}
          </span>
        </div>

        <!-- Derecha: botones de página -->
        <div class="d-flex align-items-center gap-1">
          <!-- Primera página -->
          <button
            class="btn-pag"
            :disabled="paginaActual === 1"
            @click="$emit('cambiar-pagina', 1)"
            title="Primera página"
          >
            <i class="bi bi-chevron-double-left"></i>
          </button>
          <!-- Anterior -->
          <button
            class="btn-pag"
            :disabled="paginaActual === 1"
            @click="$emit('cambiar-pagina', paginaActual - 1)"
          >
            <i class="bi bi-chevron-left"></i>
          </button>

          <!-- Páginas numéricas -->
          <template v-for="p in paginasVisibles" :key="p">
            <span v-if="p === '...'" class="pag-ellipsis">…</span>
            <button
              v-else
              class="btn-pag"
              :class="{ 'btn-pag-active': p === paginaActual }"
              @click="$emit('cambiar-pagina', p)"
            >
              {{ p }}
            </button>
          </template>

          <!-- Siguiente -->
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="$emit('cambiar-pagina', paginaActual + 1)"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
          <!-- Última página -->
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="$emit('cambiar-pagina', totalPaginas)"
            title="Última página"
          >
            <i class="bi bi-chevron-double-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- ══ CARDS MÓVIL ═════════════════════════════════════════ -->
    <div class="d-lg-none">
      <div
        v-for="p in pedidos"
        :key="'mob-' + p.numero_pedido"
        class="divisan-mobile-card mb-3 p-3 shadow-sm"
      >
        <div class="d-flex justify-content-between align-items-start mb-2">
          <div>
            <span class="badge-molde me-2">{{ p.numero_pedido }}</span>
            <span class="cliente-num">{{ p.cliente_numero }}</span>
          </div>
          <div class="d-flex gap-2">
            <span :class="badgePrioridad(p.prioridad)">{{ p.prioridad }}</span>
            <span :class="badgeDias(p.dias_restantes)">{{
              labelDias(p.dias_restantes)
            }}</span>
          </div>
        </div>
        <div class="cliente-nombre mb-1">{{ p.cliente }}</div>
        <div class="d-flex justify-content-between mb-1">
          <small class="text-cafe">Recibo: {{ formatFecha(p.fecha_recibo) }}</small>
          <small class="text-cafe">
            Compromiso:
            <span v-if="p.fecha_compromiso">{{ formatFecha(p.fecha_compromiso) }}</span>
            <span v-else class="fst-italic">Sin asignar</span>
          </small>
        </div>
        <div class="d-flex gap-2 mb-2">
          <span :class="badgeEstatusAlmacen(p.estatus_almacen)">{{
            labelEstatus(p.estatus_almacen)
          }}</span>
          <span :class="badgeEstatusCliente(p.estatus_cliente)">{{
            labelEstatusCliente(p.estatus_cliente)
          }}</span>
        </div>
        <div class="d-flex gap-2">
          <button class="btn btn-sm btn-ver" @click="$emit('ver-detalle', p)">
            <i class="bi bi-list-ul me-1"></i>Ver detalle
          </button>
          <button
            class="btn btn-sm btn-editar"
            @click="$emit('editar', p)"
            :disabled="!esEditable(p)"
            :class="{ 'btn-circular-disabled': !esEditable(p) }"
          >
            <i class="bi bi-pencil me-1"></i>Editar
          </button>
        </div>
      </div>

      <div
        v-if="!loading && pedidos.length === 0"
        class="text-center py-5 text-cafe opacity-50 fw-bold"
      >
        No se encontraron pedidos
      </div>

      <!-- Paginación móvil -->
      <div
        class="pagination-mobile d-flex justify-content-between align-items-center px-1 py-2 mt-1"
      >
        <button
          class="btn-pag-mobile"
          :disabled="paginaActual === 1"
          @click="$emit('cambiar-pagina', paginaActual - 1)"
        >
          <i class="bi bi-chevron-left"></i> Anterior
        </button>
        <small class="texto-claro fw-bold">{{ paginaActual }} / {{ totalPaginas }}</small>
        <button
          class="btn-pag-mobile"
          :disabled="paginaActual === totalPaginas"
          @click="$emit('cambiar-pagina', paginaActual + 1)"
        >
          Siguiente <i class="bi bi-chevron-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { usePedidoBadges } from "../Pedidos/composables/usePedidoBadges.js";
import ColumnFilterMenu from "@/components/Shared/ColumnFilterMenu.vue";

defineProps({
  pedidos: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  paginaActual: { type: Number, default: 1 },
  totalPaginas: { type: Number, default: 1 },
  totalRegistros: { type: Number, default: 0 },
  rangoInicio: { type: Number, default: 0 },
  rangoFin: { type: Number, default: 0 },
  paginasVisibles: { type: Array, default: () => [] },
  sortKey: { type: String, default: "fecha_recibo" },
  sortDir: { type: String, default: "desc" },
  porPagina: { type: Number, default: 10 },
  opcionesPorPagina: { type: Array, default: () => [5, 10, 15, 20] },
  itemsParaFiltroEstatus: { type: Array, default: () => [] },
  filtroEstatusSet: { type: Set, default: () => new Set() },
});

defineEmits([
  "ver-detalle",
  "editar",
  "cambiar-pagina",
  "update:sort",
  "update:filtroEstatusSet",
  "cambiar-por-pagina",
]);

const {
  formatFecha,
  labelDias,
  badgeDias,
  badgePrioridad,
  badgeEstatusAlmacen,
  badgeEstatusCliente,
  labelEstatus,
  labelEstatusCliente,
} = usePedidoBadges();

function esEditable(p) {
  return p.estatus_almacen !== "Cancelado";
}
</script>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}
.text-verde-oscuro {
  color: var(--d-green-dark);
}
.texto-claro {
  color: var(--d-cream);
}

.fecha-texto {
  color: var(--d-green);
  font-weight: 700;
  font-size: 0.9rem;
}

/* ── Tabla ──────────────────────────────────────────────── */
.divisan-table-container {
  background-color: var(--d-cream);
  border-radius: 15px;
  overflow: hidden;
}
.table-divisan thead th {
  background: var(--d-cream);
  color: var(--d-brown);
  border-bottom: 3px solid var(--d-brown);
  padding: 12px 10px;
  font-weight: 800;
  border-top: none;
}
.table-divisan td {
  border-bottom: 1px solid rgba(96, 108, 56, 0.1);
  padding: 10px 8px;
}
.table-divisan tbody tr:hover td {
  background-color: rgba(96, 108, 56, 0.04);
}

/*
 * Anchos fijos por columna (colgroup en el template) para que la tabla
 * no se estire por el texto largo de Cliente ni por los encabezados —
 * los títulos de columna se envuelven a 2 renglones en vez de forzar
 * el ancho con una sola línea.
 */
.table-pedidos {
  table-layout: fixed;
  width: 100%;
}
.table-pedidos :deep(.cfm) {
  white-space: normal;
  min-width: 0;
}
.table-pedidos :deep(.cfm-label) {
  white-space: normal;
  line-height: 1.2;
  min-width: 0;
}

/* ── Cliente cell ───────────────────────────────────────── */
.cliente-cell {
  display: flex;
  flex-direction: column;
  gap: 1px;
  max-width: 140px;
}
.cliente-num {
  font-family: var(--d-font-mono);
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--d-green);
}
.cliente-nombre {
  font-size: 0.62rem;
  color: var(--d-brown);
  font-weight: 800;
  text-transform: capitalize;
  line-height: 1.3;
  white-space: normal;
  word-break: break-word;
}

/* ── Badges ─────────────────────────────────────────────── */
.badge-molde {
  background: none;
  color: var(--d-brown);
  padding: 0;
  border-radius: 0;
  font-family: var(--d-font-mono);
  font-size: 0.88rem;
  font-weight: 600;
  opacity: 0.6;
}
.badge-prioridad {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: capitalize;
}
.badge-prioridad.alta {
  background: #fdecea;
  color: #c0392b;
}
.badge-prioridad.media {
  background: #fff8e1;
  color: #b7770d;
}
.badge-prioridad.baja {
  background: #e8f5e9;
  color: #2e7d32;
}

.badge-estatus {
  padding: 3px 8px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 800;
  white-space: normal;
  word-break: break-word;
  display: inline-block;
  text-align: center;
  max-width: 120px;
  line-height: 1.3;
}
.badge-estatus.en_proceso {
  background: #e3f2fd;
  color: #1565c0;
}
.badge-estatus.listo {
  background: #e8f5e9;
  color: #2e7d32;
}
.badge-estatus.entregado {
  background: #e8f5e9;
  color: #1b5e20;
}
.badge-estatus.cancelado {
  background: #fce4ec;
  color: #880e4f;
}
.badge-estatus.atrasado {
  background: #fdecea;
  color: #c0392b;
}
.badge-estatus.en_tiempo {
  background: #e8f5e9;
  color: #2e7d32;
}
.badge-estatus.entrega_en_tiempo {
  background: #e0f2f1;
  color: #004d40;
}
.badge-estatus.entrega_con_atraso {
  background: #fff3e0;
  color: #e65100;
}
.badge-estatus.sin-fecha {
  background: rgba(64, 34, 8, 0.07);
  color: rgba(64, 34, 8, 0.45);
  font-style: italic;
}

/* ── Botones acción ──────────────────────────────────────── */
.btn-circular {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.2s;
}
.btn-circular:hover {
  background: var(--d-green);
  color: var(--d-cream);
}
.btn-circular-disabled {
  opacity: 0.35;
  cursor: not-allowed;
  pointer-events: none;
}

.btn-ver {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
}
.btn-ver:hover {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}

.btn-editar {
  background-color: #e8f5e9;
  color: var(--d-green-dark);
  border: 1px solid var(--d-green);
  font-weight: 600;
}
.btn-editar:hover {
  background-color: var(--d-green);
  color: var(--d-cream);
}

/* ── Cards móvil ─────────────────────────────────────────── */
.divisan-mobile-card {
  background-color: var(--d-cream);
  border-radius: 15px;
  border-left: 6px solid var(--d-green);
}

/* ── Footer de paginación ────────────────────────────────── */
.pagination-wrapper {
  border-top: 1px solid rgba(64, 34, 8, 0.1);
  background-color: rgba(96, 108, 56, 0.05);
  flex-wrap: wrap;
  gap: 10px;
}

.pag-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--d-brown);
}
.pag-rango {
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--d-green);
}

/* Selector de registros por página */
.por-pagina-wrapper {
  position: relative;
}
.por-pagina-select {
  appearance: none;
  -webkit-appearance: none;
  background-color: white;
  border: 1px solid var(--d-green);
  border-radius: 8px;
  color: var(--d-brown);
  font-size: 0.82rem;
  font-weight: 700;
  padding: 4px 28px 4px 10px;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath fill='%23606c38' d='M5 7L0 2h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 9px center;
}
.por-pagina-select:focus {
  outline: none;
  border-color: var(--d-green-dark);
  box-shadow: 0 0 0 2px rgba(96, 108, 56, 0.2);
}

/* Botones numéricos de página */
.btn-pag {
  background: white;
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  border-radius: 8px;
  min-width: 34px;
  height: 34px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-pag:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-pag:disabled {
  opacity: 0.35;
  cursor: default;
}
.btn-pag-active {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
  border-color: var(--d-green-dark);
}

.pag-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 34px;
  color: var(--d-brown);
  font-weight: 700;
  font-size: 0.85rem;
}

/* ── Paginación móvil ────────────────────────────────────── */
.pagination-mobile {
  border-top: 1px solid rgba(64, 34, 8, 0.15);
}
.btn-pag-mobile {
  background: var(--d-cream);
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 110px;
  justify-content: center;
}
.btn-pag-mobile:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-pag-mobile:disabled {
  opacity: 0.35;
  cursor: default;
}
</style>
